import React, { useState } from "react";
import {
  Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import { makeStyles } from "@material-ui/core/styles";
import TuneIcon from "@material-ui/icons/Tune";

import api from "../../../../services/api";
import { DataTable, OsLink, Panel, dateLabel, daysLabel } from "./shared";

const LIMIT_FIELDS = [
  { key: "waitingClient", status: "Aguardando cliente" },
  { key: "waitingInternal", status: "Aguardando interno" },
  { key: "inValidation", status: "Em validação" },
  { key: "inProgress", status: "Em andamento" }
];

const useStyles = makeStyles(theme => ({
  grid: {
    display: "grid", gap: theme.spacing(2), gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    [theme.breakpoints.down("md")]: { gridTemplateColumns: "minmax(0, 1fr)" }
  },
  header: {
    display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap",
    gap: theme.spacing(1), marginBottom: theme.spacing(2)
  },
  count: { fontVariantNumeric: "tabular-nums", fontWeight: 600 },
  fields: { display: "grid", gap: theme.spacing(2), gridTemplateColumns: "repeat(2, minmax(0, 1fr))", paddingTop: theme.spacing(1) }
}));

const LimitsDialog = ({ open, limits, onClose, onSaved }) => {
  const classes = useStyles();
  const [values, setValues] = useState(limits);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const invalid = LIMIT_FIELDS.some(({ key }) => !/^\d+$/.test(String(values[key])) || Number(values[key]) > 365);

  const save = async () => {
    setSaving(true);
    setError("");
    try {
      const payload = LIMIT_FIELDS.reduce((result, { key }) => ({ ...result, [key]: Number(values[key]) }), {});
      await api.put("/dashboard/implantation/alert-limits", payload);
      onSaved();
    } catch (err) {
      setError(err?.response?.data?.error || "Não foi possível salvar os limites.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="xs" fullWidth
      TransitionProps={{ onEnter: () => { setValues(limits); setError(""); } }}>
      <DialogTitle>Limites dos alertas</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="textSecondary">
          Dias sem alteração a partir dos quais a etapa aparece no alerta. Vale para todos os usuários da empresa.
        </Typography>
        <div className={classes.fields}>
          {LIMIT_FIELDS.map(({ key, status }) => (
            <TextField key={key} id={`implantation-limit-${key}`} label={status} type="number" variant="outlined" size="small"
              inputProps={{ min: 0, max: 365, step: 1 }} value={values[key]}
              onChange={event => setValues(previous => ({ ...previous, [key]: event.target.value }))} />
          ))}
        </div>
        {invalid && <Typography variant="caption" color="error">Use números inteiros de 0 a 365.</Typography>}
        {error && <Alert severity="error" style={{ marginTop: 8 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancelar</Button>
        <Button onClick={save} color="primary" variant="contained" disabled={saving || invalid}>Salvar</Button>
      </DialogActions>
    </Dialog>
  );
};

const AlertsTab = ({ data, isAdmin, onLimitsSaved }) => {
  const classes = useStyles();
  const [dialogOpen, setDialogOpen] = useState(false);
  const total = data.alerts.reduce((sum, alert) => sum + alert.itens.length, 0);

  return (
    <div>
      <div className={classes.header}>
        <Typography variant="body2" color="textSecondary">
          <span className={classes.count}>{total}</span> etapa(s) paradas além do limite. Dias parada = hoje menos a data da última alteração da OS; qualquer edição zera a contagem.
        </Typography>
        {isAdmin && (
          <Button variant="outlined" size="small" startIcon={<TuneIcon />} onClick={() => setDialogOpen(true)}>
            Configurar limites
          </Button>
        )}
      </div>
      <div className={classes.grid}>
        {data.alerts.map(alert => (
          <Panel key={alert.status} title={`${alert.status} · ${alert.itens.length}`}
            subtitle={`Alerta a partir de ${daysLabel(alert.limite)} parada.`}>
            <DataTable
              rows={alert.itens}
              rowKey={row => row.os_id}
              emptyText="Nenhuma etapa neste alerta."
              maxHeight={360}
              columns={[
                { key: "dias_parada", label: "Parada", numeric: true, render: row => daysLabel(row.dias_parada) },
                { key: "os_id", label: "OS", render: row => <OsLink id={row.os_id} /> },
                { key: "cliente", label: "Cliente" },
                { key: "rotulo", label: "Etapa" },
                { key: "tecnico", label: "Técnico", render: row => row.tecnico || "-" },
                { key: "alterada_em", label: "Última alteração", render: row => dateLabel(row.alterada_em) }
              ]}
            />
          </Panel>
        ))}
      </div>
      {isAdmin && (
        <LimitsDialog open={dialogOpen} limits={data.alert_limits} onClose={() => setDialogOpen(false)}
          onSaved={() => { setDialogOpen(false); onLimitsSaved(); }} />
      )}
    </div>
  );
};

export default AlertsTab;
